import { computed, type Ref, ref } from 'vue';
import {
  GlobalMessageIds,
  type SystemMessages,
  UserBroadcastImportanceGroup,
  type UserBroadcastMessageEmergency,
  type UserBroadcastMessageWarning,
} from '@/types/systemSettings.types';
import { useRoute } from 'vue-router';
import { EMERGENCY_PAGE_EXCLUSIONS } from '@/config';

export type ExistingMessagesId = Record<UserBroadcastImportanceGroup, string[]>;

/**
 * Contient des références à tous les messages à diffuser aux utilisateurs par catégories de message.
 */
const messages = {
  [UserBroadcastImportanceGroup.emergencies]: ref<UserBroadcastMessageEmergency[]>([]),
  [UserBroadcastImportanceGroup.warnings]: ref<UserBroadcastMessageWarning[]>([]),
};

/**
 * Ajoute un message dans une catégorie
 */
const addMessage = <T extends UserBroadcastMessageEmergency | UserBroadcastMessageWarning>(message: T, messageGroup: Ref<T[]>, atBeginning: boolean = false) => {
  const index = messageGroup.value.findIndex(msg => msg.id === message.id);
  // Le message existe déjà, met à jour le message seulement si son contenu où son titre est différent.
  if (index !== -1) {
    const oldMessage = messageGroup.value[index];
    if (oldMessage && (oldMessage.content !== message.content || oldMessage?.title !== message?.title)) {
      messageGroup.value[index] = message;
    }
    return;
  }
  const method = atBeginning ? 'unshift' : 'push';
  messageGroup.value[method](message);
};

const removeMessage = (messageGroup: UserBroadcastImportanceGroup, messageId: string) => {
  const index = messages[UserBroadcastImportanceGroup[messageGroup]].value.findIndex((entry: UserBroadcastMessageEmergency | UserBroadcastMessageWarning) => entry.id === messageId);
  if (index !== -1) {
    messages[UserBroadcastImportanceGroup[messageGroup]].value.splice(index, 1);
  }
};

const idsDifference = (newIds: Set<string>, oldIds: Set<string>): Set<string> => {
  const result = new Set<string>();
  oldIds.forEach(oldId => {
    if (!newIds.has(oldId)) {
      result.add(oldId);
    }
  });
  return result;
};

export default function useUserBroadcastMessages() {
  const route = useRoute();
  /**
   * Le message de maintenance doit toujours sortir en premier,
   * Les messages de problèmes réseaux ou proxy potentiels de QA doivent sortir en deuxième.
   * Certaines routes ne doivent pas diffuser les messages d'urgences tel que les étapes des formulaires.
   */
  const sortEmergencies = (messages: UserBroadcastMessageEmergency[]): UserBroadcastMessageEmergency[] => {
    const showEmergencies = route && typeof route.name === 'string' ? !EMERGENCY_PAGE_EXCLUSIONS.includes(route.name) : true;
    let newMessages = [...messages];
    if (newMessages.length && !showEmergencies) {
      newMessages = newMessages.filter(message => message.id in GlobalMessageIds);
    }
    if (newMessages.length > 1) {
      const reverseIds = [...Object.keys(GlobalMessageIds)].reverse();
      reverseIds.forEach(messageId => {
        const targetMessageIndex = newMessages.findIndex(message => message.id === messageId);
        if (targetMessageIndex > 0) {
          const [messageToMove] = newMessages.splice(targetMessageIndex, 1);
          if (messageToMove) {
            newMessages.unshift(messageToMove);
          }
        }
      });
    }
    return newMessages;
  };

  const addEmergency = (emergency: UserBroadcastMessageEmergency, atBeginning: boolean = false) => addMessage<UserBroadcastMessageEmergency>(emergency, messages[UserBroadcastImportanceGroup.emergencies], atBeginning);
  const addWarning = (warning: UserBroadcastMessageWarning, atBeginning: boolean = false) => addMessage<UserBroadcastMessageWarning>(warning, messages[UserBroadcastImportanceGroup.warnings], atBeginning);

  const removeEmergency = (emergencyId: string) => removeMessage(UserBroadcastImportanceGroup.emergencies, emergencyId);
  const removeWarning = (warningId: string) => removeMessage(UserBroadcastImportanceGroup.warnings, warningId);

  const emergencies = computed<UserBroadcastMessageEmergency[]>(() => {
    return sortEmergencies(messages.emergencies.value);
  });
  const warnings = computed<UserBroadcastMessageWarning[]>(() => messages.warnings.value);

  const existingMessagesId = computed<ExistingMessagesId>(() => ({
    [UserBroadcastImportanceGroup.emergencies]: emergencies.value.map(alert => alert.id),
    [UserBroadcastImportanceGroup.warnings]: warnings.value.map(warning => warning.id),
  }));

  /**
   * Supprime, pour chaque catégorie, tous les messages dont le ID ne se trouve pas dans la liste des nouveaux messages
   * reçus en paramètre newMessages en excluant automatiquement les ID globaux tel que pour le message de maintenance
   * et les messages d'états de réseaux.
   *
   * Lorsque le paramètre contient un objet vide, tous les ID de la liste active sont donc introuvable dans la nouvelle
   * liste reçue via le paramètre `newMessages` et seront donc supprimés.
   */
  const cleanOldMessages = (newMessages: Partial<SystemMessages>) => {
    const newMessagesId = {
      [UserBroadcastImportanceGroup.emergencies]: new Set((newMessages.emergencies ?? []).map(emergency => emergency.id)),
      [UserBroadcastImportanceGroup.warnings]: new Set((newMessages.warnings ?? []).map(warning => warning.id)),
    };
    const oldMessagesId = {
      /** Le group de ID existant doit aussi exclure les ID globaux pour les maintenance et problème de réseaux */
      [UserBroadcastImportanceGroup.emergencies]: new Set(existingMessagesId.value.emergencies.filter(emergencyId => !(emergencyId in GlobalMessageIds))),
      [UserBroadcastImportanceGroup.warnings]: new Set(existingMessagesId.value.warnings),
    };
    idsDifference(newMessagesId[UserBroadcastImportanceGroup.emergencies], oldMessagesId[UserBroadcastImportanceGroup.emergencies]).forEach((emergencyId: string) => removeEmergency(emergencyId));
    idsDifference(newMessagesId[UserBroadcastImportanceGroup.warnings], oldMessagesId[UserBroadcastImportanceGroup.warnings]).forEach((warningId: string) => removeWarning(warningId));
  };

  return {
    emergencies,
    warnings,
    existingMessagesId,
    addEmergency,
    addWarning,
    removeEmergency,
    removeWarning,
    cleanOldMessages,
  };
}
